// Vendors
import { useEffect, useMemo, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useWebSocketHook } from '@openbank/ziniaweb-ui-react-features';
// Constants
import {
  IFRAME_WS_CONFIGURATION,
  IFRAME_WS_LIMIT_CONFIGURATION,
  IFRAME_WS_MESSAGE_PROPS,
} from './constants/iframe.hook.constants';
// Event Handlers
import { IframeHandlers } from '../handlers/iframe.handlers';
import { IframeErrorHandlers } from '../handlers/iframe-error.handlers';
// Types
import { IframeComponentType } from 'components/iframe/types/iframe.component.type';
import { IframeHookType } from './types/iframe.hook.type';

const useIframeHook = ({
  onReceiveDataHandler: onReceiveData,
  webSocketMessage,
  id,
  onEventListen,
}: Omit<IframeComponentType, 'frameGap' | 'src'>): IframeHookType => {
  const [frameLoaded, setFrameLoaded] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(true);
  const [errorAttempts, setErrorAttempts] = useState<number>(0);
  const [iframeKey, setIframeKey] = useState<number>(0);
  const [wsRetry, setWsRetry] = useState<boolean>(false);

  const { showBoundary: setError } = useErrorBoundary();

  const { handleCatchAndManageFrameComponentErrorEvent } = useMemo<
    ReturnType<typeof IframeErrorHandlers>
  >(() => IframeErrorHandlers({ errorAttempts, id, setError, setErrorAttempts }), [errorAttempts]);

  const { webSocketConnected } = onReceiveData
    ? useWebSocketHook({
        ...IFRAME_WS_CONFIGURATION,
        initMessage: {
          ...IFRAME_WS_MESSAGE_PROPS,
          ...webSocketMessage,
        },
        limitConfiguration: {
          ...IFRAME_WS_LIMIT_CONFIGURATION,
          onLimitTime: handleCatchAndManageFrameComponentErrorEvent,
        },
        onReceiveData,
        onError: handleCatchAndManageFrameComponentErrorEvent,
        retryMount: wsRetry,
      })
    : { webSocketConnected: true };

  const {
    handleEvaluateIframeComponentHealthyEvent,
    handleCheckFrameComponentLoadSuccessEvent,
    handleRemoveIframeEventListener,
  } = useMemo<ReturnType<typeof IframeHandlers>>(
    () =>
      IframeHandlers({
        errorAttempts,
        frameLoaded,
        onEventListen,
        setFrameLoaded,
        setFetching,
        setIframeKey,
        setWsRetry,
        webSocketConnected,
      }),
    [frameLoaded, errorAttempts, webSocketConnected]
  );
  // Idea here is to evaluate when iframe is ready to be used, which means it has loaded and the WebSocket is connected, but also to handle errors that may occur during the loading process or WebSocket connection.
  useEffect(() => {
    handleEvaluateIframeComponentHealthyEvent();
    return () => {
      handleRemoveIframeEventListener();
    };
  }, [frameLoaded, errorAttempts, webSocketConnected]);

  return {
    fetching,
    handleCheckFrameComponentLoadSuccessEvent,
    handleCatchAndManageFrameComponentErrorEvent,
    iframeKey,
  };
};

export { useIframeHook };
